import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useYouTubeData } from '../../contexts/AppDataContext';
import { useAuth } from '../auth/AuthContext';
import { toastHelper } from '../../utils/toastHelper';
import { cn } from '@/lib/utils';
import './UploadDefaultsContainer.css';

export interface UploadDefault {
  id?: number;
  name: string;
  video_title?: string;
  video_description?: string;
  tags?: string[];
  category?: string;
  category_name?: string;
  playlist_id?: string;
  playlist_name?: string;
  privacy_status?: string;
  license?: string;
  made_for_kids?: boolean;
  embeddable?: boolean;
  scheduled?: boolean;
  disable_watermark?: boolean;
}

interface UploadDefaultsContainerProps {
  values: UploadDefault;
  onChange: (updates: Partial<UploadDefault>) => void;
}

const UploadDefaultsContainer: React.FC<UploadDefaultsContainerProps> = ({ values, onChange }) => {
  const { isAuthenticated } = useAuth();
  const { youtubeData, fetchYouTubeData } = useYouTubeData();
  const [categories, setCategories] = useState<{ id: string, name: string }[]>([]);
  const [playlists, setPlaylists] = useState<{ id: string, name: string }[]>([]);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const [tagLimitExceeded, setTagLimitExceeded] = useState(false);

  // Fetch YouTube data on mount (explicit)
  // Only fetch if data is null (not empty object from 401 error)
  useEffect(() => {
    if (youtubeData === null && isAuthenticated) {
      fetchYouTubeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only on mount

  useEffect(() => {
    if (youtubeData) {
      setCategories(youtubeData.categories || []);
      setPlaylists(youtubeData.playlists || []);
    }
  }, [youtubeData]);

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = tagInputRef.current?.value.trim();
      if (input) {
        const inputTags = input.split(',').map(t => t.trim()).filter(t => t && !(values.tags || []).includes(t));
        const currentLength = (values.tags || []).join(',').length;
        const inputLength = inputTags.join(',').length;
        if (currentLength + inputLength <= 500) {
          onChange({ tags: [...(values.tags || []), ...inputTags] });
          setTagLimitExceeded(false);
        } else {
          setTagLimitExceeded(true);
          toastHelper.newMessage('error', 'Tag Limit Exceeded', 'Total tags length cannot exceed 500 characters.');
          setTimeout(() => setTagLimitExceeded(false), 1500);
        }
        if (tagInputRef.current) tagInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6 py-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Enter default name"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="video-title">YouTube Video Title</Label>
        <Input
          id="video-title"
          value={values.video_title || ''}
          onChange={(e) => onChange({ video_title: e.target.value })}
          placeholder="Enter video title"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="video-description">YouTube Video Description</Label>
        <Textarea
          id="video-description"
          value={values.video_description || ''}
          onChange={(e) => onChange({ video_description: e.target.value })}
          placeholder="Enter video description"
          rows={6}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="tags">Add Tags (Enter or comma-separated)</Label>
        <Input
          id="tags"
          ref={tagInputRef}
          onKeyDown={handleTagKeyDown}
          placeholder="Type tags and press Enter"
          className={cn(tagLimitExceeded && 'border-destructive')}
        />
        {tagLimitExceeded && (
          <p className="text-sm text-destructive">Max total tag length is 500 characters</p>
        )}
      </div>

      {(values.tags || []).length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Tags ({values.tags?.length})</Label>
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'text-xs',
                  values.tags!.join(',').length >= 490
                    ? 'text-destructive'
                    : values.tags!.join(',').length >= 450
                      ? 'text-orange-500'
                      : 'text-muted-foreground'
                )}
              >
                {values.tags!.join(',').length}/500
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange({ tags: [] })}
                className="h-auto p-0 text-xs text-destructive hover:text-destructive"
              >
                Clear All
              </Button>
            </div>
          </div>
          <div className="tags-wrapper-modal flex flex-wrap gap-2">
            {values.tags?.map((tag, idx) => (
              <Badge 
                key={idx} 
                variant="outline" 
                className="flex items-center gap-1.5 border-purple-500/50 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded-full px-3 py-1 text-xs font-medium"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => onChange({ tags: values.tags!.filter(t => t !== tag) })}
                  className="ml-0.5 rounded-full hover:bg-purple-500/30 p-0.5 transition-colors"
                  aria-label={`Remove tag ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="category-playlist-container grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Category</Label>
          <Select
            value={values.category || '10'}
            onValueChange={(id) => {
              const name = categories.find(c => c.id === id)?.name || '';
              onChange({ category: id, category_name: name });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {!categories.find(cat => cat.id === '10') && (
                <SelectItem value="10">Default</SelectItem>
              )}
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Playlist</Label>
          <Select
            value={values.playlist_id || 'none'}
            onValueChange={(id) => {
              if (id === 'none') {
                onChange({ playlist_id: '', playlist_name: '' });
              } else {
                const name = playlists.find(p => p.id === id)?.name || '';
                onChange({ playlist_id: id, playlist_name: name });
              }
            }}
            disabled={playlists.length === 0}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select playlist" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {playlists.map(pl => (
                <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Label>Privacy Status</Label>
            <RadioGroup
              value={values.privacy_status || 'private'}
              onValueChange={(value) => onChange({ privacy_status: value })}
              className="flex flex-row gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="public" id="privacy-public" />
                <Label htmlFor="privacy-public" className="cursor-pointer">Public</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="private" id="privacy-private" />
                <Label htmlFor="privacy-private" className="cursor-pointer">Private</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="unlisted" id="privacy-unlisted" />
                <Label htmlFor="privacy-unlisted" className="cursor-pointer">Unlisted</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label>License</Label>
            <RadioGroup
              value={values.license || 'youtube'}
              onValueChange={(value) => onChange({ license: value })}
              className="flex flex-row gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="youtube" id="license-youtube" />
                <Label htmlFor="license-youtube" className="cursor-pointer">YouTube</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="creativeCommon" id="license-cc" />
                <Label htmlFor="license-cc" className="cursor-pointer">Creative Commons</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-3">
            <Label>Made for Kids</Label>
            <RadioGroup
              value={values.made_for_kids ? 'yes' : 'no'}
              onValueChange={(value) => onChange({ made_for_kids: value === 'yes' })}
              className="flex flex-row gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="kids-yes" />
                <Label htmlFor="kids-yes" className="cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="kids-no" />
                <Label htmlFor="kids-no" className="cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-3">
            <Label>Remove Watermark</Label>
            <RadioGroup
              value={values.disable_watermark !== false ? 'yes' : 'no'}
              onValueChange={(value) => onChange({ disable_watermark: value === 'yes' })}
              className="flex flex-row gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="watermark-yes" />
                <Label htmlFor="watermark-yes" className="cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="watermark-no" />
                <Label htmlFor="watermark-no" className="cursor-pointer">No</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDefaultsContainer;